import type { ReactElement } from "react";
import type { Slot } from "@/shared/dashboard/types";
import type {
  WidgetDefinition,
  WidgetId,
  WidgetProps,
} from "@/shared/dashboard/widgets/types";

/**
 * Widgets register themselves here at module load via side-effect imports
 * from the DashboardShell. Initially empty — populated by widget modules
 * calling `registerWidget(def)` at their own module scope.
 */
export const WIDGET_REGISTRY: Partial<Record<WidgetId, WidgetDefinition>> = {};

export function registerWidget(def: WidgetDefinition): void {
  if (WIDGET_REGISTRY[def.id]) {
    const msg = `Widget id "${def.id}" is already registered. Registry is append-only.`;
    if (import.meta.env?.DEV) throw new Error(msg);
    console.warn(msg);
    return;
  }
  WIDGET_REGISTRY[def.id] = def;
}

export type PlacementResult =
  | { ok: true; definition: WidgetDefinition }
  | { ok: false; reason: string };

export function validateWidgetPlacement(
  id: WidgetId,
  slot: Slot,
): PlacementResult {
  const def = WIDGET_REGISTRY[id];
  if (!def) {
    return { ok: false, reason: `unknown widget id "${id}"` };
  }
  if (!def.supportedSlots.has(slot)) {
    return {
      ok: false,
      reason: `widget "${id}" not supported in slot "${slot}" (supports: ${[...def.supportedSlots].join(", ")})`,
    };
  }
  return { ok: true, definition: def };
}

/**
 * Render a widget by id into a slot.
 * Dev: throws on any mismatch (unknown id, unsupported slot).
 * Prod: returns null + console.warn.
 */
export function renderWidget(
  id: WidgetId,
  props: WidgetProps,
): ReactElement | null {
  const result = validateWidgetPlacement(id, props.slot);
  if (!result.ok) {
    const msg = `[dashboard] ${result.reason}`;
    if (import.meta.env?.DEV) throw new Error(msg);
    console.warn(msg);
    return null;
  }
  const Cmp = result.definition.component;
  return <Cmp {...props} />;
}
